import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  ShoppingCart,
  RefreshCw,
  Download,
  Search,
  Filter,
  BarChart3,
  AlertCircle,
  FileText,
  Building2,
  ChevronDown
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const AdminAnalyticsReportingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const isInitialMount = useRef(true);
  
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days by default
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Analytics data state
  const [bookingsData, setBookingsData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [manualToursData, setManualToursData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    total_bookings: 0,
    total_revenue: 0,
    total_guests: 0,
    average_booking_value: 0
  });
  const [tourDistribution, setTourDistribution] = useState([]);
  
  // Tour Log state
  const [tourLog, setTourLog] = useState([]);
  const [loadingTourLog, setLoadingTourLog] = useState(false);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Tour source filter for manual tours
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'oasis', 'noble', 'manual'
  const [sourceStats, setSourceStats] = useState({
    oasis: { total_entries: 0, total_guests: 0 },
    noble: { total_entries: 0, total_guests: 0 },
    manual: { total_entries: 0, total_guests: 0 }
  });

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showPdfDropdown, setShowPdfDropdown] = useState(false);

  // Tour source colors and labels
  const tourSourceConfig = {
    oasis: { label: 'Oasis', color: 'emerald', bgClass: 'bg-emerald-500', lightBg: 'bg-emerald-50', textClass: 'text-emerald-700' },
    noble: { label: 'Noble', color: 'sky', bgClass: 'bg-sky-500', lightBg: 'bg-sky-50', textClass: 'text-sky-700' },
    manual: { label: 'Manual', color: 'purple', bgClass: 'bg-purple-500', lightBg: 'bg-purple-50', textClass: 'text-purple-700' }
  };

  // Auto-refresh every 30 seconds for real-time data
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAnalyticsData(false); // Silent refresh
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  useEffect(() => {
    // Only show full loading state on initial mount
    const showLoading = isInitialMount.current;
    fetchAnalyticsData(showLoading);
    fetchTourLog();
    fetchSourceStats();

    // Mark initial mount as complete after first load
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [startDate, endDate]);

  // Re-fetch tour log when source filter changes (no full page loading)
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchTourLog();
    }
  }, [sourceFilter]);

  // Update tour time distribution from tour log (online + walk-in guests only)
  useEffect(() => {
    if (tourLog.length > 0) {
      const timeDistribution = { '10:00': 0, '14:00': 0, '16:00': 0 };

      tourLog.forEach(log => {
        if (timeDistribution.hasOwnProperty(log.time)) {
          // Add online and walk-in guests (not manual)
          timeDistribution[log.time] += (log.online_count || 0) + (log.walkin_count || 0);
        }
      });

      const tourDistData = [
        { name: '10:00 AM', value: timeDistribution['10:00'], color: '#8b5cf6' },
        { name: '2:00 PM', value: timeDistribution['14:00'], color: '#3b82f6' },
        { name: '4:00 PM', value: timeDistribution['16:00'], color: '#06b6d4' }
      ];

      setTourDistribution(tourDistData);
    }
  }, [tourLog]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchAnalyticsData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setRefreshing(!showLoading);
      const headers = getAuthHeaders();
      if (!headers) return;

      // Fetch tour bookings data
      const cacheBuster = `_t=${Date.now()}`;
      const bookingsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;
      const bookingsResponse = await fetch(bookingsUrl, { 
        headers: {
          ...headers,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!bookingsResponse.ok) {
        throw new Error('Failed to fetch bookings data');
      }

      const bookingsResult = await bookingsResponse.json();
      
      // Process bookings data for charts
      processBookingsData(bookingsResult.bookings || []);
      
      // Note: Tour logs are fetched separately via fetchTourLog()
      // This keeps analytics and tour logs independent
      
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  const processBookingsData = (bookings) => {
    // Group bookings by date for line chart
    const bookingsByDate = {};
    const revenueByDate = {};
    let totalRevenue = 0;
    let totalGuests = 0;
    const timeDistribution = { '10:00': 0, '14:00': 0, '16:00': 0 };

    bookings.forEach(booking => {
      const date = booking.tour_date;
      
      // Bookings by date
      bookingsByDate[date] = (bookingsByDate[date] || 0) + 1;
      
      // Revenue by date
      const revenue = booking.total_amount || 0;
      revenueByDate[date] = (revenueByDate[date] || 0) + revenue;
      totalRevenue += revenue;
      
      // Guest count
      totalGuests += booking.guest_count || 0;
      
      // Time distribution
      if (booking.tour_time && timeDistribution.hasOwnProperty(booking.tour_time)) {
        timeDistribution[booking.tour_time] += 1;
      }
    });

    // Convert to chart data
    const bookingsChartData = Object.entries(bookingsByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
        bookings: count
      }));

    const revenueChartData = Object.entries(revenueByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, amount]) => ({
        date: new Date(date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
        revenue: amount
      }));

    const tourDistData = [
      { name: '10:00 AM', value: timeDistribution['10:00'], color: '#8b5cf6' },
      { name: '2:00 PM', value: timeDistribution['14:00'], color: '#3b82f6' },
      { name: '4:00 PM', value: timeDistribution['16:00'], color: '#06b6d4' }
    ];

    setBookingsData(bookingsChartData);
    setRevenueData(revenueChartData);
    setTourDistribution(tourDistData);
    setSummaryStats({
      total_bookings: bookings.length,
      total_revenue: totalRevenue,
      total_guests: totalGuests,
      average_booking_value: bookings.length > 0 ? totalRevenue / bookings.length : 0
    });
  };

  const fetchTourLog = async () => {
    setLoadingTourLog(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Add cache buster to ensure fresh data
      const cacheBuster = `_t=${Date.now()}`;
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/tours/log?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;

      // Add source filter if not 'all'
      if (sourceFilter !== 'all') {
        url += `&source=${sourceFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tour log');
      }

      const data = await response.json();
      setTourLog(data.tour_log || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTourLog(false);
    }
  };

  const fetchSourceStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const cacheBuster = `_t=${Date.now()}`;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours/analytics?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns stats under 'by_source' key
        setSourceStats(data.by_source || data);
      }
    } catch (err) {
      console.error('Error fetching source stats:', err);
    }
  };

  const handleExportTourLogPDF = async (source = 'all') => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Add cache buster
      const cacheBuster = `_t=${Date.now()}`;
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/tours/log/export-pdf?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;

      // Add source filter if not 'all'
      if (source !== 'all') {
        apiUrl += `&source=${source}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      // Download PDF
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const sourceSuffix = source !== 'all' ? `_${source}` : '';
      a.download = `tour_log${sourceSuffix}_${startDate}_to_${endDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      setShowPdfDropdown(false);
    } catch (err) {
      setError(`Failed to export PDF: ${err.message}`);
    }
  };

  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start = new Date();

    switch (preset) {
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setDate(today.getDate() - 30);
        break;
      case '3months':
        start.setMonth(today.getMonth() - 3);
        break;
      case '6months':
        start.setMonth(today.getMonth() - 6);
        break;
      case '12months':
        start.setMonth(today.getMonth() - 12);
        break;
      case 'ytd':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      case 'all':
        start = new Date(2020, 0, 1); // Set to a very early date
        break;
      default:
        start.setDate(today.getDate() - 30);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleExportData = () => {
    // Simple CSV export
    const csvData = [
      ['Date Range', `${startDate} to ${endDate}`],
      ['Total Bookings', summaryStats.total_bookings],
      ['Total Revenue', `$${summaryStats.total_revenue.toFixed(2)}`],
      ['Total Guests', summaryStats.total_guests],
      ['Average Booking Value', `$${summaryStats.average_booking_value.toFixed(2)}`]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${startDate}_to_${endDate}.csv`;
    a.click();
  };

  const handleExportTourLogCSV = (source = 'all') => {
    // Filter tour log based on source
    let filteredLog = tourLog;
    if (source !== 'all') {
      filteredLog = tourLog.filter(log => {
        // Filter manual_tours by source
        const hasMatchingManual = log.manual_tours.some(mt => mt.tour_source === source);
        return hasMatchingManual;
      }).map(log => ({
        ...log,
        manual_tours: log.manual_tours.filter(mt => mt.tour_source === source),
        manual_count: log.manual_tours.filter(mt => mt.tour_source === source).reduce((sum, mt) => sum + mt.guest_count, 0)
      }));
    }

    // Export tour log as CSV
    const headers = ['Date', 'Time', 'Source', 'Online', 'Walk-in', 'Manual', 'Total', 'Staff Assigned', 'Notes'];

    const rows = filteredLog.map(log => {
      const manualNotes = log.manual_tours
        .map(mt => {
          const parts = [];
          if (mt.tour_source) parts.push(`[${mt.tour_source.toUpperCase()}]`);
          if (mt.staff_member) parts.push(`Staff: ${mt.staff_member}`);
          if (mt.notes) parts.push(mt.notes);
          return parts.join(' - ');
        })
        .filter(note => note)
        .join('; ');

      return [
        formatDateDisplay(log.date),
        log.time_display,
        source === 'all' ? 'All' : source.charAt(0).toUpperCase() + source.slice(1),
        log.online_count,
        log.walkin_count,
        log.manual_count,
        log.total_count + log.manual_count,
        log.staff_member || '-',
        `"${manualNotes || '-'}"` // Wrap in quotes to handle commas
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const sourceSuffix = source !== 'all' ? `_${source}` : '';
    a.download = `tour_log${sourceSuffix}_${startDate}_to_${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button asChild variant="ghost" size="sm">
                <div onClick={() => navigate('/admin/dashboard')} className="cursor-pointer flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </div>
              </Button>
              <div className="flex items-center gap-3">
                <div className="bg-purple-600 w-10 h-10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Analytics & Reporting</h1>
                  <p className="text-sm text-gray-500">Real-time insights and data analysis</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExportData}
                variant="outline"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button onClick={() => fetchAnalyticsData()} disabled={refreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {/* Date Range Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Date Range:</span>
              </div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <Button
                onClick={() => {
                  const date = new Date();
                  setEndDate(date.toISOString().split('T')[0]);
                  date.setDate(date.getDate() - 30);
                  setStartDate(date.toISOString().split('T')[0]);
                }}
                variant="outline"
                size="sm"
              >
                Last 30 Days
              </Button>
              <Button
                onClick={() => {
                  const date = new Date();
                  setEndDate(date.toISOString().split('T')[0]);
                  date.setDate(date.getDate() - 7);
                  setStartDate(date.toISOString().split('T')[0]);
                }}
                variant="outline"
                size="sm"
              >
                Last 7 Days
              </Button>
              {refreshing && (
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Updating...
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Total Bookings</p>
                  <p className="text-3xl font-bold mt-2">{summaryStats.total_bookings}</p>
                </div>
                <Calendar className="w-12 h-12 text-purple-200" />
              </div>
              <div className="mt-4 text-purple-100 text-sm">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Active bookings
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Total Revenue</p>
                  <p className="text-3xl font-bold mt-2">${summaryStats.total_revenue.toFixed(2)}</p>
                </div>
                <DollarSign className="w-12 h-12 text-green-200" />
              </div>
              <div className="mt-4 text-green-100 text-sm">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Tour ticket sales
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Guests</p>
                  <p className="text-3xl font-bold mt-2">{summaryStats.total_guests}</p>
                </div>
                <Users className="w-12 h-12 text-blue-200" />
              </div>
              <div className="mt-4 text-blue-100 text-sm">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Tour participants
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Avg Booking Value</p>
                  <p className="text-3xl font-bold mt-2">${summaryStats.average_booking_value.toFixed(2)}</p>
                </div>
                <ShoppingCart className="w-12 h-12 text-orange-200" />
              </div>
              <div className="mt-4 text-orange-100 text-sm">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Per booking
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Bookings Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Bookings Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={bookingsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="bookings" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Revenue Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Tour Time Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Tour Time Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tourDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {tourDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 flex justify-center gap-4">
                {tourDistribution.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-gray-600">{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats Table */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Booking Rate</span>
                  <span className="text-lg font-bold text-purple-600">
                    {summaryStats.total_bookings > 0 ? '100%' : '0%'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Revenue Growth</span>
                  <span className="text-lg font-bold text-green-600">
                    ${summaryStats.total_revenue.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Avg Guests per Booking</span>
                  <span className="text-lg font-bold text-blue-600">
                    {summaryStats.total_bookings > 0 
                      ? (summaryStats.total_guests / summaryStats.total_bookings).toFixed(1)
                      : '0'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Peak Time</span>
                  <span className="text-lg font-bold text-orange-600">
                    {tourDistribution.length > 0
                      ? tourDistribution.reduce((max, item) => item.value > max.value ? item : max).name
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comprehensive Tour Log Section */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-indigo-600" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Comprehensive Tour Log</h3>
                  <p className="text-sm text-gray-600 mt-1">Complete overview of all tour activities</p>
                </div>
              </div>
              <div className="flex gap-2">
                {/* CSV Export Dropdown */}
                <div className="relative">
                  <Button
                    onClick={() => { setShowExportDropdown(!showExportDropdown); setShowPdfDropdown(false); }}
                    disabled={loadingTourLog}
                    variant="outline"
                    className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export CSV
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                  {showExportDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      <button onClick={() => handleExportTourLogCSV('all')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg">All Tours</button>
                      <button onClick={() => handleExportTourLogCSV('oasis')} className="w-full px-4 py-2 text-left text-sm hover:bg-emerald-50 text-emerald-700">Oasis Tours Only</button>
                      <button onClick={() => handleExportTourLogCSV('noble')} className="w-full px-4 py-2 text-left text-sm hover:bg-sky-50 text-sky-700">Noble Tours Only</button>
                      <button onClick={() => handleExportTourLogCSV('manual')} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 text-purple-700 rounded-b-lg">Manual Tours Only</button>
                    </div>
                  )}
                </div>

                {/* PDF Export Dropdown */}
                <div className="relative">
                  <Button
                    onClick={() => { setShowPdfDropdown(!showPdfDropdown); setShowExportDropdown(false); }}
                    disabled={loadingTourLog}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loadingTourLog ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export PDF
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                  {showPdfDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      <button onClick={() => handleExportTourLogPDF('all')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg text-gray-700">All Tours</button>
                      <button onClick={() => handleExportTourLogPDF('oasis')} className="w-full px-4 py-2 text-left text-sm hover:bg-emerald-50 text-emerald-700">Oasis Tours Only</button>
                      <button onClick={() => handleExportTourLogPDF('noble')} className="w-full px-4 py-2 text-left text-sm hover:bg-sky-50 text-sky-700">Noble Tours Only</button>
                      <button onClick={() => handleExportTourLogPDF('manual')} className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 text-purple-700 rounded-b-lg">Manual Tours Only</button>
                    </div>
                  )}
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Quick Date Range Filters */}
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-700 w-full sm:w-auto mb-2 sm:mb-0">Date Range:</p>
              <Button
                onClick={() => setDateRangePreset('week')}
                variant="outline"
                size="sm"
              >
                This Week
              </Button>
              <Button
                onClick={() => setDateRangePreset('month')}
                variant="outline"
                size="sm"
              >
                This Month
              </Button>
              <Button
                onClick={() => setDateRangePreset('3months')}
                variant="outline"
                size="sm"
              >
                3 Months
              </Button>
              <Button
                onClick={() => setDateRangePreset('6months')}
                variant="outline"
                size="sm"
              >
                6 Months
              </Button>
              <Button
                onClick={() => setDateRangePreset('12months')}
                variant="outline"
                size="sm"
              >
                12 Months
              </Button>
              <Button
                onClick={() => setDateRangePreset('ytd')}
                variant="outline"
                size="sm"
              >
                Year to Date
              </Button>
              <Button
                onClick={() => setDateRangePreset('all')}
                variant="outline"
                size="sm"
              >
                All Time
              </Button>
            </div>

            {/* Tour Source Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-6 pb-6 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-700 w-full sm:w-auto mb-2 sm:mb-0">Tour Source:</p>
              <Button
                onClick={() => setSourceFilter('all')}
                variant={sourceFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                className={sourceFilter === 'all' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              >
                All Tours
              </Button>
              <Button
                onClick={() => setSourceFilter('oasis')}
                variant={sourceFilter === 'oasis' ? 'default' : 'outline'}
                size="sm"
                className={sourceFilter === 'oasis' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
              >
                <Building2 className="w-4 h-4 mr-1" />
                Oasis
              </Button>
              <Button
                onClick={() => setSourceFilter('noble')}
                variant={sourceFilter === 'noble' ? 'default' : 'outline'}
                size="sm"
                className={sourceFilter === 'noble' ? 'bg-sky-600 hover:bg-sky-700' : 'border-sky-300 text-sky-700 hover:bg-sky-50'}
              >
                <Building2 className="w-4 h-4 mr-1" />
                Noble
              </Button>
              <Button
                onClick={() => setSourceFilter('manual')}
                variant={sourceFilter === 'manual' ? 'default' : 'outline'}
                size="sm"
                className={sourceFilter === 'manual' ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}
              >
                Manual
              </Button>
              {loadingTourLog && (
                <span className="text-sm text-gray-500 flex items-center gap-2 ml-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading...
                </span>
              )}
            </div>

            {/* Tour Log Summary Cards - Updated with Source Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-4 rounded-lg">
                <p className="text-indigo-100 text-sm font-medium">Total Tours</p>
                <p className="text-3xl font-bold mt-2">
                  {tourLog.filter(log => log.total_count > 0 || log.manual_count > 0).length}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-200" />
                  <p className="text-emerald-100 text-sm font-medium">Oasis Tours</p>
                </div>
                <p className="text-3xl font-bold mt-2">
                  {sourceStats.oasis?.total_guests || 0}
                </p>
                <p className="text-emerald-200 text-xs mt-1">{sourceStats.oasis?.total_entries || 0} entries</p>
              </div>
              <div className="bg-gradient-to-br from-sky-500 to-sky-600 text-white p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-sky-200" />
                  <p className="text-sky-100 text-sm font-medium">Noble Tours</p>
                </div>
                <p className="text-3xl font-bold mt-2">
                  {sourceStats.noble?.total_guests || 0}
                </p>
                <p className="text-sky-200 text-xs mt-1">{sourceStats.noble?.total_entries || 0} entries</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg">
                <p className="text-purple-100 text-sm font-medium">Manual Entries</p>
                <p className="text-3xl font-bold mt-2">
                  {sourceStats.manual?.total_guests || 0}
                </p>
                <p className="text-purple-200 text-xs mt-1">{sourceStats.manual?.total_entries || 0} entries</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white p-4 rounded-lg">
                <p className="text-amber-100 text-sm font-medium">Walk-ins</p>
                <p className="text-3xl font-bold mt-2">
                  {tourLog.reduce((sum, log) => sum + log.walkin_count, 0)}
                </p>
              </div>
            </div>

            {/* Tour Log Table */}
            {loadingTourLog ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading tour log...</p>
              </div>
            ) : tourLog.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No tour data available for this date range</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-indigo-600 text-white">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Time</th>
                      {sourceFilter !== 'all' && (
                        <th className="px-4 py-3 text-left text-sm font-semibold">Source</th>
                      )}
                      <th className="px-4 py-3 text-center text-sm font-semibold">Online</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Walk-in</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Manual</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Total</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Staff Assigned</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...tourLog]
                      .filter(log => {
                        // When a source filter is active, only show rows with manual entries
                        if (sourceFilter !== 'all') {
                          return log.manual_count > 0;
                        }
                        // When showing all, show rows that have any activity
                        return log.total_count > 0 || log.manual_count > 0;
                      })
                      .sort((a, b) => {
                        // Sort by date descending (newest first), then by time descending
                        if (a.date !== b.date) {
                          return b.date.localeCompare(a.date);
                        }
                        // Then sort by time descending
                        return b.time.localeCompare(a.time);
                      })
                      .map((log, index) => {
                        const manualNotes = log.manual_tours
                          .map(mt => {
                            const parts = [];
                            if (mt.staff_member) parts.push(`Staff: ${mt.staff_member}`);
                          if (mt.notes) parts.push(mt.notes);
                          return parts.join(' - ');
                        })
                        .filter(note => note)
                        .join('; ');
                      
                      return (
                        <tr
                          key={index}
                          className={`border-b border-gray-200 hover:bg-gray-50 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {formatDateDisplay(log.date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {log.time_display}
                          </td>
                          {sourceFilter !== 'all' && (
                            <td className="px-4 py-3">
                              <Badge className={`text-xs font-semibold ${
                                sourceFilter === 'oasis' ? 'bg-emerald-100 text-emerald-700' :
                                sourceFilter === 'noble' ? 'bg-sky-100 text-sky-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {sourceFilter.charAt(0).toUpperCase() + sourceFilter.slice(1)}
                              </Badge>
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                              {log.online_count}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              {log.walkin_count}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              {log.manual_count}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                              {log.total_count + log.manual_count}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {log.staff_member ? (
                              <span className="text-indigo-700 font-semibold">
                                {log.staff_member}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 italic max-w-xs truncate">
                            {manualNotes || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          Auto-refreshes every 30 seconds • Last updated: {new Date().toLocaleTimeString('en-AU')}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsReportingPage;
